/*
  RedHeal Lab hero – «Datafelt – én prikk per studie».
  Unit chart on <canvas>: one dot per publication, stacked in year columns (1980–2026).
  RCTs on humans (per_year[].rct) are drawn in the accent colour at the bottom of each column.
  Motion: one-off settle from a loose cloud (~2.2 s), then a barely visible shimmer on ~1.5 % of dots.
  Motion is off for prefers-reduced-motion or when the wrapper has data-motion="off".
*/
window.RHLab = window.RHLab || {};
if (!window.RHLab.loadData) {
  const cache = new Map();
  window.RHLab.loadData = (url) => {
    if (!cache.has(url)) cache.set(url, fetch(url, { credentials: 'omit' }).then((r) => { if (!r.ok) throw new Error('rh-lab data ' + r.status); return r.json(); }));
    return cache.get(url);
  };
  window.RHLab.fmt = (n) => new Intl.NumberFormat('nb-NO').format(n);
}

(() => {
  if (customElements.get('rh-lab-field')) return;

  const Y0 = 1980;
  const Y1 = 2026;
  const SETTLE_MS = 2200;
  const SHIMMER_SHARE = 0.015;
  const SHIMMER_FPS = 24;
  const COL_GAP = 0.9; // gap between year columns, in dot pitches
  const MONO = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const clamp01 = (t) => (t < 0 ? 0 : t > 1 ? 1 : t);

  // Deterministic PRNG so layout/cloud is stable between redraws.
  const rng = (seed) => () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Resolve any CSS colour (incl. color-mix) to [r,g,b,a] via a probe element.
  function parseColor(str) {
    let m = str.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)/);
    if (m) return [+m[1], +m[2], +m[3], m[4] === undefined ? 1 : parseAlpha(m[4])];
    m = str.match(/color\(srgb\s+([\d.e-]+)\s+([\d.e-]+)\s+([\d.e-]+)(?:\s*\/\s*([\d.]+%?))?\s*\)/);
    if (m) return [m[1] * 255, m[2] * 255, m[3] * 255, m[4] === undefined ? 1 : parseAlpha(m[4])];
    return null;
  }
  function parseAlpha(a) {
    return a.endsWith('%') ? parseFloat(a) / 100 : +a;
  }
  const rgba = (c, a = 1) => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${(c[3] * a).toFixed(3)})`;
  const mix = (a, b, t) => [0, 1, 2, 3].map((i) => a[i] + (b[i] - a[i]) * t);

  class RHLabField extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.canvas = this.querySelector('canvas');
      this.tip = this.querySelector('.rhlab-hero-field__tip');
      if (!this.canvas || !this.canvas.getContext) return;
      this.ctx = this.canvas.getContext('2d');
      this.hero = this.closest('.rhlab-hero');
      this.visible = false;
      this.state = 'idle'; // idle → settling → done
      this.hover = -1;
      this.raf = 0;

      this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onMq = () => { if (!this.motionAllowed()) this.finish(); else this.tick(); };
      this.mq.addEventListener?.('change', this._onMq);

      this._onVis = () => (document.hidden ? this.stop() : this.tick());
      document.addEventListener('visibilitychange', this._onVis);

      this._onMove = (e) => this.onPointer(e);
      this._onLeave = () => this.setHover(-1);
      this.canvas.addEventListener('pointermove', this._onMove);
      this.canvas.addEventListener('pointerdown', this._onMove);
      this.canvas.addEventListener('pointerleave', this._onLeave);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries[entries.length - 1].isIntersecting;
          if (this.visible) this.tick(); else this.stop();
        }, { threshold: 0.15 });
        this.io.observe(this);
      } else {
        this.visible = true;
      }

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.resize());
        this.ro.observe(this);
      }

      const src = this.dataset.src;
      if (!src) return;
      window.RHLab.loadData(src).then((data) => {
        if (!this.isConnected) return;
        this.setData(data);
      }).catch(() => {});
    }

    disconnectedCallback() {
      this.stop();
      this.io?.disconnect();
      this.ro?.disconnect();
      this.mq?.removeEventListener?.('change', this._onMq);
      document.removeEventListener('visibilitychange', this._onVis);
      this.canvas?.removeEventListener('pointermove', this._onMove);
      this.canvas?.removeEventListener('pointerdown', this._onMove);
      this.canvas?.removeEventListener('pointerleave', this._onLeave);
      this._init = false;
    }

    motionAllowed() {
      if (this.mq?.matches) return false;
      if (this.hero && this.hero.dataset.motion === 'off') return false;
      return true;
    }

    setData(data) {
      const rows = (data.per_year || []).filter((d) => d.y >= Y0 && d.y <= Y1).sort((a, b) => a.y - b.y);
      if (!rows.length) return;
      this.years = rows;
      this.partial = data.meta?.partial_year;
      this.total = rows.reduce((s, d) => s + d.all, 0);
      this.totalRct = rows.reduce((s, d) => s + d.rct, 0);
      let max = rows[0];
      for (const d of rows) if (d.all > max.all) max = d;
      this.peak = max;

      const fmt = window.RHLab.fmt;
      const first = rows[0];
      const last = rows[rows.length - 1];
      this.canvas.setAttribute(
        'aria-label',
        `Prikkdiagram med ${fmt(this.total)} publikasjoner om rødt og nær-infrarødt lys fordelt på utgivelsesår ${first.y}–${last.y}, én prikk per publikasjon. ` +
          `Antallet har økt fra ${fmt(first.all)} i ${first.y} til ${fmt(max.all)} i ${max.y}` +
          (this.partial === last.y ? `, og ${fmt(last.all)} hittil i ${last.y}. ` : '. ') +
          `Røde prikker er randomiserte kontrollerte studier på mennesker, ${fmt(this.totalRct)} totalt.`
      );

      if (!this.motionAllowed()) this.state = 'done';
      this.resize(true);
      this.tick();
    }

    // ---------- layout ----------
    resize(force) {
      if (!this.years) return;
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      if (!force && w === this.w && h === this.h && dpr === this.dpr) return;
      this.w = w; this.h = h; this.dpr = dpr;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.readColors();
      this.layout();
      this.staticLayer = null;
      if (this.state !== 'settling') this.drawFinal();
    }

    readColors() {
      const cs = getComputedStyle(this);
      const probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;visibility:hidden';
      this.appendChild(probe);
      const get = (v, fb) => {
        probe.style.color = '';
        probe.style.color = cs.getPropertyValue(v).trim() || fb;
        return parseColor(getComputedStyle(probe).color) || parseColor(fb) || [0, 0, 0, 1];
      };
      this.cDot = get('--rhf-dot', 'rgb(190,184,176)');
      this.cRct = get('--rhf-rct', 'rgb(232,28,49)');
      this.cAxis = get('--rhf-axis', 'rgba(0,0,0,0.38)');
      this.cLabel = get('--rhf-label', 'rgba(0,0,0,0.8)');
      probe.remove();
      // Shimmer targets: grey dots warm towards the accent, red dots lift slightly.
      this.cDotHi = mix(this.cDot, this.cRct, 0.55);
      this.cRctHi = mix(this.cRct, [255, 255, 255, 1], 0.3);
    }

    layout() {
      const { w, h, years } = this;
      const small = w < 480;
      const padTop = small ? 26 : 34; // room for the peak annotation
      const padBottom = small ? 22 : 26; // baseline + year labels
      const plotW = w;
      const plotH = h - padTop - padBottom;
      const N = years.length;
      const maxN = this.peak.all;

      // Choose dots-per-row (k) so the tallest column fits and dots are as large as possible.
      let best = { p: 0, k: 1 };
      for (let k = 1; k <= 40; k++) {
        const pw = plotW / (N * k + (N - 1) * COL_GAP);
        const ph = plotH / Math.ceil(maxN / k);
        const p = Math.min(pw, ph);
        if (p > best.p) best = { p, k };
      }
      const { p, k } = best;
      // Use the leftover space on the free axis too, keeping dots close to a square grid.
      const pw = plotW / (N * k + (N - 1) * COL_GAP);
      const ph = plotH / Math.ceil(maxN / k);
      const px = Math.min(pw, p * 1.3);
      const py = Math.min(ph, p * 1.3);
      const colW = k * px;
      const step = colW + COL_GAP * px;
      const chartW = N * colW + (N - 1) * COL_GAP * px;
      const x0 = (plotW - chartW) / 2;
      const base = padTop + plotH;

      const total = this.total;
      const tx = new Float32Array(total);
      const ty = new Float32Array(total);
      const sx = new Float32Array(total);
      const sy = new Float32Array(total);
      const dl = new Float32Array(total);
      const col = new Uint16Array(total);
      const red = new Uint8Array(total);
      const R = rng(20260930);
      let i = 0;
      this.cols = years.map((d, c) => {
        const cx = x0 + c * step;
        const rowsN = Math.ceil(d.all / k);
        for (let j = 0; j < d.all; j++) {
          const r = Math.floor(j / k);
          const q = j % k;
          tx[i] = cx + (q + 0.5) * px;
          ty[i] = base - (r + 0.5) * py;
          // Loose cloud: scattered above and around the final position, drifting in from the left.
          const u = R();
          const v = R();
          sx[i] = tx[i] + (u - 0.5) * w * 0.22 - w * 0.04;
          sy[i] = padTop + (v * v) * plotH * 0.85 - plotH * 0.18;
          // Keep the cloud inside the canvas (reflect off the edges instead of clipping).
          if (sx[i] > w - 3) sx[i] = 2 * (w - 3) - sx[i];
          if (sx[i] < 3) sx[i] = 6 - sx[i];
          if (sy[i] < 3) sy[i] = 6 - sy[i];
          dl[i] = (c / N) * 0.42 + R() * 0.16; // share of SETTLE_MS
          col[i] = c;
          red[i] = j < d.rct ? 1 : 0;
          i++;
        }
        return { x: cx, w: colW, top: base - rowsN * py, d };
      });
      Object.assign(this, { p, px, k, x0, base, padTop, plotH, chartW, tx, ty, sx, sy, dl, col, red, small });
      this.dot = p * 0.74;
      this.round = p * this.dpr >= 4;

      // Shimmer set
      const n = Math.max(12, Math.round(total * SHIMMER_SHARE));
      this.shim = [];
      for (let s = 0; s < n; s++) {
        this.shim.push({ i: Math.floor(R() * total), ph: R() * Math.PI * 2, per: 3200 + R() * 4200 });
      }
    }

    // ---------- drawing ----------
    dotPath(ctx, x, y) {
      const r = this.dot / 2;
      if (this.round) {
        ctx.moveTo(x + r, y);
        ctx.arc(x, y, r, 0, Math.PI * 2);
      } else {
        ctx.rect(x - r, y - r, this.dot, this.dot);
      }
    }

    colAlpha(c) {
      let a = this.years[c].y === this.partial ? 0.5 : 1;
      if (this.hover >= 0 && this.hover !== c) a *= 0.35;
      return a;
    }

    // Draw all dots. progress: null = final positions; otherwise ms since start.
    drawDots(ctx, elapsed) {
      const { tx, ty, sx, sy, dl, red } = this;
      const anim = elapsed != null;
      const N = this.years.length;
      // Group by column and colour so we only switch fillStyle ~94 times.
      let i = 0;
      for (let c = 0; c < N; c++) {
        const n = this.years[c].all;
        const end = i + n;
        for (let pass = 1; pass >= 0; pass--) {
          const baseC = pass ? this.cRct : this.cDot;
          let a = this.colAlpha(c);
          if (anim) {
            const colT = clamp01((elapsed / SETTLE_MS - (c / N) * 0.2) / 0.14);
            a *= colT;
            if (a <= 0.001) continue;
          }
          ctx.fillStyle = rgba(baseC, a);
          ctx.beginPath();
          for (let j = i; j < end; j++) {
            if (red[j] !== pass) continue;
            let x = tx[j];
            let y = ty[j];
            if (anim) {
              const t = easeOut(clamp01((elapsed / SETTLE_MS - dl[j]) / 0.42));
              x = sx[j] + (x - sx[j]) * t;
              y = sy[j] + (y - sy[j]) * t;
            }
            this.dotPath(ctx, x, y);
          }
          ctx.fill();
        }
        i = end;
      }
    }

    drawAxis(ctx, alpha = 1) {
      const { x0, chartW, base, cols, small } = this;
      ctx.save();
      ctx.globalAlpha = alpha;
      const hair = 1 / this.dpr;
      ctx.fillStyle = rgba(this.cAxis);
      ctx.fillRect(x0, base + 3, chartW, Math.max(hair, 1 / this.dpr));

      const fs = small ? 10 : 11;
      ctx.font = `400 ${fs}px ${MONO}`;
      ctx.textBaseline = 'top';
      ctx.fillStyle = rgba(this.cLabel);
      const ticks = [Y0, 2000, Y1];
      for (const y of ticks) {
        const c = cols.find((cc) => cc.d.y === y);
        if (!c) continue;
        const cx = c.x + c.w / 2;
        ctx.fillStyle = rgba(this.cAxis);
        ctx.fillRect(Math.round(cx * this.dpr) / this.dpr, base + 3, hair, 4);
        ctx.fillStyle = rgba(this.cLabel);
        ctx.textAlign = y === Y0 ? 'left' : y === Y1 ? 'right' : 'center';
        const lx = y === Y0 ? c.x : y === Y1 ? c.x + c.w : cx;
        ctx.fillText(String(y), lx, base + 10);
      }

      // Quiet annotation on the tallest column.
      const pk = cols.find((cc) => cc.d === this.peak);
      if (pk) {
        const fmt = window.RHLab.fmt;
        const label = `${pk.d.y} · ${fmt(pk.d.all)} publikasjoner`;
        const tx = pk.x + pk.w / 2;
        const ty = pk.top - 7;
        ctx.fillStyle = rgba(this.cAxis);
        ctx.fillRect(Math.round(tx * this.dpr) / this.dpr, ty - 2, hair, 6);
        ctx.fillStyle = rgba(this.cLabel);
        ctx.textBaseline = 'alphabetic';
        const tw = ctx.measureText(label).width;
        // Right-align to the column when it sits near the right edge.
        let lx = tx - tw / 2;
        if (lx + tw > this.w) lx = pk.x + pk.w - tw;
        if (lx < 0) lx = 0;
        ctx.textAlign = 'left';
        ctx.fillText(label, lx, ty - 6);
      }
      ctx.restore();
    }

    begin(ctx) {
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.clearRect(0, 0, this.w, this.h);
    }

    drawFinal() {
      if (!this.cols) return;
      const ctx = this.ctx;
      this.begin(ctx);
      this.drawDots(ctx, null);
      this.drawAxis(ctx);
      this.canvas.classList.add('is-drawn');
      // Cache the resting frame for the shimmer loop (only without hover dimming).
      if (this.hover < 0 && typeof OffscreenCanvas !== 'undefined') {
        try {
          const off = new OffscreenCanvas(this.canvas.width, this.canvas.height);
          off.getContext('2d').drawImage(this.canvas, 0, 0);
          this.staticLayer = off;
        } catch (e) {
          this.staticLayer = null;
        }
      } else if (this.hover < 0) {
        const off = document.createElement('canvas');
        off.width = this.canvas.width;
        off.height = this.canvas.height;
        off.getContext('2d').drawImage(this.canvas, 0, 0);
        this.staticLayer = off;
      }
    }

    drawShimmer(now) {
      if (!this.staticLayer) this.drawFinal();
      if (!this.staticLayer) return;
      const ctx = this.ctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.drawImage(this.staticLayer, 0, 0);
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      const { tx, ty, red, col } = this;
      for (const s of this.shim) {
        const v = Math.sin(now / s.per * Math.PI * 2 + s.ph);
        const a = v > 0.55 ? (v - 0.55) / 0.45 : 0; // lit only part of the cycle
        if (a <= 0.02) continue;
        const i = s.i;
        const base = red[i] ? this.cRct : this.cDot;
        const hi = red[i] ? this.cRctHi : this.cDotHi;
        const colA = this.years[col[i]].y === this.partial ? 0.5 : 1;
        ctx.fillStyle = rgba(mix(base, hi, a * 0.85), colA);
        ctx.beginPath();
        this.dotPath(ctx, tx[i], ty[i]);
        ctx.fill();
      }
    }

    // ---------- loop ----------
    tick() {
      if (this.raf || !this.cols) return;
      if (!this.visible || document.hidden) return;
      if (!this.motionAllowed()) {
        if (this.state !== 'done') this.finish();
        return;
      }
      if (this.state === 'idle') {
        this.state = 'settling';
        this.t0 = performance.now();
      }
      let last = 0;
      const frame = (now) => {
        this.raf = 0;
        if (!this.visible || document.hidden) return;
        if (this.state === 'settling') {
          const el = now - this.t0;
          if (el >= SETTLE_MS * 1.02) {
            this.state = 'done';
            this.drawFinal();
          } else {
            const ctx = this.ctx;
            this.begin(ctx);
            this.drawDots(ctx, el);
            this.drawAxis(ctx, clamp01((el - SETTLE_MS * 0.35) / (SETTLE_MS * 0.4)));
            this.canvas.classList.add('is-drawn');
          }
        } else if (this.hover < 0 && now - last >= 1000 / SHIMMER_FPS) {
          last = now;
          this.drawShimmer(now);
        }
        this.raf = requestAnimationFrame(frame);
      };
      this.raf = requestAnimationFrame(frame);
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    finish() {
      this.stop();
      this.state = 'done';
      this.staticLayer = null;
      this.drawFinal();
    }

    // ---------- hover ----------
    onPointer(e) {
      if (!this.cols || this.state === 'settling') return;
      const r = this.canvas.getBoundingClientRect();
      const x = e.clientX - r.left;
      const step = this.k * this.px + COL_GAP * this.px;
      const c = Math.floor((x - this.x0 + (COL_GAP * this.px) / 2) / step);
      this.setHover(c >= 0 && c < this.cols.length ? c : -1);
    }

    setHover(c) {
      if (c === this.hover) return;
      this.hover = c;
      if (!this.cols) return;
      if (c < 0) {
        if (this.tip) this.tip.hidden = true;
        this.staticLayer = null;
        this.drawFinal();
        this.tick();
        return;
      }
      this.stop();
      const ctx = this.ctx;
      this.begin(ctx);
      this.drawDots(ctx, null);
      this.drawAxis(ctx);
      const cc = this.cols[c];
      if (this.tip) {
        const fmt = window.RHLab.fmt;
        const d = cc.d;
        const yl = d.y === this.partial ? `${d.y} (hittil i år)` : `${d.y}`;
        this.tip.textContent = `${yl} · ${fmt(d.all)} publikasjoner · ${fmt(d.rct)} RCT`;
        this.tip.hidden = false;
        const tw = this.tip.offsetWidth;
        const th = this.tip.offsetHeight;
        let lx = cc.x + cc.w / 2 - tw / 2;
        lx = Math.max(0, Math.min(this.w - tw, lx));
        const ly = Math.max(0, cc.top - th - 10);
        this.tip.style.transform = `translate(${Math.round(lx)}px, ${Math.round(ly)}px)`;
      }
    }
  }

  customElements.define('rh-lab-field', RHLabField);
})();
